import { apiFetch } from '../client';
import type {
    LevelEntityWire,
    LevelEntityListItemLocal,
    LevelNameLocal,
    CreateLevelEntityRequestLocal,
    UpdateLevelEntityRequestLocal,
    ListLevelEntitiesParamsLocal,
} from '../types/LevelEntityLocal';
import { normalizeLevelEntityList, normalizeLevelEntity } from '../types/levelEntityNormalizers';

interface RequestOpts {
    idempotencyKey?: string;
}

// Helpers locales duplicados por consistencia del repo (igual que tenant.ts/retail.ts/platform.ts).
function toSearch(params: Record<string, string | number | undefined>): string {
    const entries = Object.entries(params).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return '';
    const usp = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
    return `?${usp.toString()}`;
}

function withIdempotency(opts: RequestOpts): RequestInit {
    return opts.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : {};
}

/**
 * Client tipado de las entidades de NIVEL genéricas. Path real verificado en `backoffice.gen.ts`:
 *   GET/POST   `/tenants/{tenantId}/levels/{level}/entities`        (list ?parentId / create)
 *   GET/PUT/DELETE `/tenants/{tenantId}/levels/{level}/entities/{id}` (detail / update / delete)
 *
 * `level` = LEVEL_1 | LEVEL_2 | LEVEL_3 (tipado `LevelNameLocal`). El list devuelve
 * `{ items?: LevelEntity[] }` SIN cursor (no hay paginación en el contrato). `apiFetch` ya
 * desenvuelve `data`; el normalizer transforma epoch ms → ISO. Gateado server-side
 * (`tenant.level.manage` / el operador del nivel; el backend re-enforza).
 */
export const levelEntityApi = {
    list: (
        tenantId: string,
        level: LevelNameLocal,
        params: ListLevelEntitiesParamsLocal = {},
    ): Promise<{ items: LevelEntityListItemLocal[] }> =>
        apiFetch<{ items?: LevelEntityWire[] }>(
            `/tenants/${tenantId}/levels/${level}/entities${toSearch(params)}`,
        ).then(normalizeLevelEntityList),

    create: (
        tenantId: string,
        level: LevelNameLocal,
        body: CreateLevelEntityRequestLocal,
        opts: RequestOpts = {},
    ): Promise<LevelEntityListItemLocal> =>
        apiFetch<LevelEntityWire>(`/tenants/${tenantId}/levels/${level}/entities`, {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }).then(normalizeLevelEntity),

    get: (tenantId: string, level: LevelNameLocal, id: string): Promise<LevelEntityListItemLocal> =>
        apiFetch<LevelEntityWire>(`/tenants/${tenantId}/levels/${level}/entities/${id}`).then(
            normalizeLevelEntity,
        ),

    update: (
        tenantId: string,
        level: LevelNameLocal,
        id: string,
        body: UpdateLevelEntityRequestLocal,
    ): Promise<LevelEntityListItemLocal> =>
        apiFetch<LevelEntityWire>(`/tenants/${tenantId}/levels/${level}/entities/${id}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }).then(normalizeLevelEntity),

    // DELETE sin body: el backend responde 200 `{ deleted: true }`. No requiere tipo generado extra.
    delete: (tenantId: string, level: LevelNameLocal, id: string) =>
        apiFetch<{ deleted?: boolean }>(`/tenants/${tenantId}/levels/${level}/entities/${id}`, {
            method: 'DELETE',
        }),
};
